import {z} from 'zod';

import {ErrorSchema, UuidResponseBodySchema} from '@dms/api/shared';

// Enums
export const TeamAssignmentStrategyEnumSchema = z.enum([
  'assign_to_lead',
  'least_recently_assigned',
]);

// Request/Response Body Schemas
export const AddTeamMembersRequestBodySchema = z.object({
  userIds: z.array(z.string()),
});

export const CreateTeamRequestBodySchema = z.object({
  name: z.string(),
  branches: z.array(z.string()),
  deputies: z.array(z.string()),
  leadId: z.string().nullable(),
  assignmentStrategy: TeamAssignmentStrategyEnumSchema,
  active: z.boolean().optional(),
  timeToAcceptAssignment: z.number().int().nullable(),
  maximumEscalations: z.number().int().nullable(),
});

export const UpdateTeamRequestBodySchema = z.object({
  name: z.string(),
  branches: z.array(z.string()),
  deputies: z.array(z.string()),
  leadId: z.string().nullable(),
  assignmentStrategy: TeamAssignmentStrategyEnumSchema,
  active: z.boolean(),
  timeToAcceptAssignment: z.number().int().nullable(),
  maximumEscalations: z.number().int().nullable(),
});

export const GetTeamResponseBodySchema = z.object({
  name: z.string(),
  branches: z.array(z.string()),
  deputies: z.array(z.string()),
  leadId: z.string().nullable(),
  assignmentStrategy: TeamAssignmentStrategyEnumSchema,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  timeToAcceptAssignment: z.number().int().nullable(),
  maximumEscalations: z.number().int().nullable(),
});

export const ListTeamsResponseBodySchema = z.object({
  id: z.string().uuid(),
  name: z.string().max(255),
});

// API Response Schemas
export const AddTeamMembersApiResponseSchema = z.void(); // status 204
export const DeactivateTeamMemberApiResponseSchema = z.void(); // status 204
export const CreateTeamApiResponseSchema = UuidResponseBodySchema; // status 201
export const DeleteTeamApiResponseSchema = z.void(); // status 204
export const GetTeamApiResponseSchema = GetTeamResponseBodySchema; // status 200
export const UpdateTeamApiResponseSchema = z.void(); // status 204
export const DeleteTeamMemberApiResponseSchema = z.void(); // status 204
export const ActivateTeamMemberApiResponseSchema = z.void(); // status 204
export const ListTeamsApiResponseSchema = z.array(ListTeamsResponseBodySchema); // status 200

// API Arguments Schemas
export const AddTeamMembersApiArgSchema = z.object({
  teamId: z.string(),
  addTeamMembersRequestBody: AddTeamMembersRequestBodySchema,
});

export const DeactivateTeamMemberApiArgSchema = z.object({
  teamId: z.string(),
  memberId: z.string(),
});

export const CreateTeamApiArgSchema = z.object({
  createTeamRequestBody: CreateTeamRequestBodySchema,
});

export const DeleteTeamApiArgSchema = z.object({
  id: z.string(),
});

export const GetTeamApiArgSchema = z.object({
  id: z.string(),
});

export const UpdateTeamApiArgSchema = z.object({
  id: z.string(),
  updateTeamRequestBody: UpdateTeamRequestBodySchema,
});

export const DeleteTeamMemberApiArgSchema = z.object({
  teamId: z.string(),
  memberId: z.string(),
});

export const ActivateTeamMemberApiArgSchema = z.object({
  teamId: z.string(),
  memberId: z.string(),
});

export const ListTeamsApiArgSchema = z.object({
  active: z.boolean().optional(),
  branchId: z.string().uuid().optional(),
});

// Exported Types (Inferred)
export type TeamAssignmentStrategyEnum = z.infer<typeof TeamAssignmentStrategyEnumSchema>;
export type AddTeamMembersRequestBody = z.infer<typeof AddTeamMembersRequestBodySchema>;
export type CreateTeamRequestBody = z.infer<typeof CreateTeamRequestBodySchema>;
export type UpdateTeamRequestBody = z.infer<typeof UpdateTeamRequestBodySchema>;
export type GetTeamResponseBody = z.infer<typeof GetTeamResponseBodySchema>;
export type ListTeamsResponseBody = z.infer<typeof ListTeamsResponseBodySchema>;
export type Error = z.infer<typeof ErrorSchema>;
export type UuidResponseBody = z.infer<typeof UuidResponseBodySchema>;

// API Response Types
export type AddTeamMembersApiResponse = z.infer<typeof AddTeamMembersApiResponseSchema>;
export type DeactivateTeamMemberApiResponse = z.infer<typeof DeactivateTeamMemberApiResponseSchema>;
export type CreateTeamApiResponse = z.infer<typeof CreateTeamApiResponseSchema>;
export type DeleteTeamApiResponse = z.infer<typeof DeleteTeamApiResponseSchema>;
export type GetTeamApiResponse = z.infer<typeof GetTeamApiResponseSchema>;
export type UpdateTeamApiResponse = z.infer<typeof UpdateTeamApiResponseSchema>;
export type DeleteTeamMemberApiResponse = z.infer<typeof DeleteTeamMemberApiResponseSchema>;
export type ActivateTeamMemberApiResponse = z.infer<typeof ActivateTeamMemberApiResponseSchema>;
export type ListTeamsApiResponse = z.infer<typeof ListTeamsApiResponseSchema>;

// API Argument Types
export type AddTeamMembersApiArg = z.infer<typeof AddTeamMembersApiArgSchema>;
export type DeactivateTeamMemberApiArg = z.infer<typeof DeactivateTeamMemberApiArgSchema>;
export type CreateTeamApiArg = z.infer<typeof CreateTeamApiArgSchema>;
export type DeleteTeamApiArg = z.infer<typeof DeleteTeamApiArgSchema>;
export type GetTeamApiArg = z.infer<typeof GetTeamApiArgSchema>;
export type UpdateTeamApiArg = z.infer<typeof UpdateTeamApiArgSchema>;
export type DeleteTeamMemberApiArg = z.infer<typeof DeleteTeamMemberApiArgSchema>;
export type ActivateTeamMemberApiArg = z.infer<typeof ActivateTeamMemberApiArgSchema>;
export type ListTeamsApiArg = z.infer<typeof ListTeamsApiArgSchema>;
